import React, { forwardRef } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { MediaSection } from '@polestar/component-warehouse-react/components/section/Section';
import Spacings, { createCustomSpacing, } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import { useSSRSafeId } from '@polestar/component-warehouse-react/contexts/ssr';
import { CrossAxisAlignments, MainAxisAlignments, } from '@polestar/component-warehouse-react/tokens/alignments/Alignments';
import { MotionDurations, MotionEasings, createCSSTransition, } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import { maximumStretchedWidth, maximumWidth, } from '@polestar/component-warehouse-react/tokens/breakpoints/Breakpoints';
import { responsiveStyles } from '@polestar/component-warehouse-react/helpers/responsiveStyle';
import Grids from '@polestar/component-warehouse-react/tokens/grids/Grids';
import { docgenEnum } from '@polestar/component-warehouse-react/helpers/docgenInterface';
export var OfferBarVariants;
(function (OfferBarVariants) {
    OfferBarVariants["default"] = "default";
    OfferBarVariants["white"] = "white";
    OfferBarVariants["black"] = "black";
})(OfferBarVariants || (OfferBarVariants = {}));
/**
 * @internal
 */
export const OfferBarVariantsInterface = docgenEnum('OfferBarVariants', OfferBarVariants);
const OfferBar = forwardRef(({ message, variant = OfferBarVariants.default, hasMaxWidth = false, ...props }, ref) => {
    const { copy, link } = message || {};
    const { label, href, onClick, target, rel } = link || {};
    const memoizedId = useSSRSafeId('offer-bar', props.id);
    return (React.createElement(OfferBarMediaSection, { ...props, ref: ref, id: memoizedId, variant: variant, hasMaxWidth: hasMaxWidth },
        React.createElement(Wrapper, null,
            React.createElement(MessageWrapper, { variant: variant, onClick: onClick, href: href, target: target, rel: rel },
                React.createElement(Message, null,
                    copy,
                    href && (React.createElement(OfferCTA, { id: `${memoizedId}-cta`, variant: variant }, label)))))));
});
OfferBar.displayName = 'OfferBar';
export default OfferBar;
const OfferBarMediaSection = styled(MediaSection)(({ theme, variant, hasMaxWidth }) => css(css `
      background-color: ${theme.offerBar.variants[variant].background};

      ${responsiveStyles({
    padding: {
        mobile: `0 ${Grids.mobile.gutter}`,
        tablet: `0 ${Grids.tablet.gutter}`,
        desktop: `0 ${Grids.desktop.gutter}`,
    },
})}
    `, !hasMaxWidth &&
    css `
        max-width: unset;
      `, hasMaxWidth &&
    css `
        max-width: ${maximumStretchedWidth};
      `));
const Wrapper = styled.div(() => css(css `
    padding-block: ${Spacings.medium};
    margin: 0 auto;

    ${responsiveStyles({
    padding: {
        mobile: `${createCustomSpacing(12 / 16)} ${Grids.mobile.gutter} ${createCustomSpacing(13 / 16)}`,
        tablet: `${createCustomSpacing(11 / 16)} ${Spacings.large}`,
        desktop: `${createCustomSpacing(11 / 16)} ${Spacings.xLarge}`,
        desktopWide: `${createCustomSpacing(11 / 16)} ${Spacings.xxxxLarge}`,
    },
    maxWidth: {
        mobile: '100%',
        desktopWide: `${maximumWidth}px`,
    },
})},
  `));
const MessageWrapper = styled.a(({ theme, variant }) => css `
    position: relative;
    color: ${theme.offerBar.variants[variant].color};
    display: flex;
    flex-wrap: wrap;
    justify-content: ${MainAxisAlignments.center};
    align-items: ${CrossAxisAlignments.center};
    width: 100%;
    text-decoration: none;
    text-align: center;
  `);
const Message = styled.p `
  ${responsiveStyles({
    fontSize: {
        mobile: Spacings.small,
        tablet: Spacings.medium,
    },
    lineHeight: {
        mobile: createCustomSpacing(14 / 16),
        tablet: createCustomSpacing(18 / 16),
    },
})};
`;
const OfferCTA = styled.span(({ theme, variant }) => css `
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding-inline-start: ${createCustomSpacing(6 / 16)};
    transition: ${createCSSTransition('color', MotionEasings.ease, MotionDurations.normal)};
    color: ${theme.offerBar.variants[variant].cta.states.default.color};
    text-decoration: underline;
    text-underline-offset: 0.125rem;

    ${responsiveStyles({
    gap: {
        mobile: Spacings.xxSmall,
        tablet: Spacings.xSmall,
    },
    lineHeight: {
        mobile: createCustomSpacing(14 / 16),
        tablet: createCustomSpacing(18 / 16),
    },
})};

    &:hover {
      color: ${theme.offerBar.variants[variant].cta.states.hover.color};
    }
  `);
